import { capturePostHogEvent } from "~/lib/posthog/client";

export type DeviceTestStep = "camera" | "mic" | "speaker" | "network";
export type AttemptReason = "initial" | "retry" | "device_change";
export type CompletionMethod =
  | "manual"
  | "automatic"
  | "skipped"
  | "continue_anyway";
export type DiagnosticPhase =
  | "get_user_media"
  | "audio_context"
  | "enumerate_devices"
  | "speaker_routing"
  | "speaker_playback"
  | "network_cleanup"
  | "network_init"
  | "network_pre_auth"
  | "network_quality";

interface DiagnosticProperties {
  step?: DeviceTestStep;
  attempt?: number;
  reason?: AttemptReason;
  method?: CompletionMethod;
  phase?: DiagnosticPhase;
  error_name?: string;
  error_type?: "permissions" | "not-found" | "device-in-use" | "unknown";
  permission_state?: PermissionState | "unsupported" | "unknown";
  track_state?: MediaStreamTrackState;
  track_enabled?: boolean;
  track_muted?: boolean;
  audio_context_state?: AudioContextState;
  result?: "good" | "warning" | "bad" | "failed" | "aborted";
  recoverable?: boolean;
}

export function recordDeviceTestEvent(
  action:
    | "started"
    | "ready"
    | "failed"
    | "result"
    | "step_completed"
    | "completed"
    | "device_changed",
  properties: DiagnosticProperties,
) {
  try {
    capturePostHogEvent(`device_test_${action}`, { ...properties });
  } catch {
    // Diagnostics must never prevent a device test or a retry.
  }
}

const ERROR_NAMES = new Set([
  "NotAllowedError",
  "PermissionDeniedError",
  "NotFoundError",
  "DevicesNotFoundError",
  "NotReadableError",
  "AbortError",
  "TrackStartError",
  "OverconstrainedError",
  "ConstraintNotSatisfiedError",
  "SecurityError",
  "InvalidStateError",
  "NotSupportedError",
  "TypeError",
  "TimeoutError",
]);

export function diagnosticErrorName(error: unknown): string {
  return error instanceof Error && ERROR_NAMES.has(error.name)
    ? error.name
    : "unknown";
}

// This is a browser permission hint, not proof of the OS-level permission.
// Query after the outcome without delaying the UI; unsupported or stuck APIs are safe.
export async function readDevicePermission(
  name: "camera" | "microphone",
): Promise<NonNullable<DiagnosticProperties["permission_state"]>> {
  if (!navigator.permissions?.query) return "unsupported";
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const status = await Promise.race([
      navigator.permissions.query({ name: name as PermissionName }),
      new Promise<null>((resolve) => {
        timeout = setTimeout(() => resolve(null), 250);
      }),
    ]);
    return status?.state === "granted" ||
      status?.state === "denied" ||
      status?.state === "prompt"
      ? status.state
      : "unknown";
  } catch {
    return "unsupported";
  } finally {
    clearTimeout(timeout);
  }
}

export async function recordMediaOutcome(
  action: "ready" | "failed",
  properties: DiagnosticProperties,
  permission: "camera" | "microphone",
  isCurrent: () => boolean,
) {
  const permissionState = await readDevicePermission(permission);
  if (isCurrent()) {
    recordDeviceTestEvent(action, {
      ...properties,
      permission_state: permissionState,
    });
  }
}
